import { httpCreateUserLien, httpReleaseUserLien } from "api/admin/user";
import { DateTime } from "luxon";
import { FormEvent, useState } from "react";
import { moneyFormat, getUserCurrency } from "utilities/currency";
import { showDanger, showSuccess } from "utilities/misc";

interface ActiveLien {
  id: number;
  amount: number | string;
  currency?: string;
  reason: string;
  reference?: string | null;
  createdAt: string;
}

export interface LienSummary {
  currency?: string;
  lienCurrency?: string;
  pendingConversion?: boolean;
  totalPortfolioBalance: number | null;
  activeLienAmount: number;
  availableBalance: number;
  shortfall: number | null;
  activeLiens: ActiveLien[];
}

interface UserLienProps {
  userID: number;
  userCountry?: string;
  lien: LienSummary;
  refetch: () => Promise<unknown>;
}

const UserLien: React.FC<UserLienProps> = ({
  userID,
  userCountry,
  lien,
  refetch,
}) => {
  const fiatCurrency =
    lien.currency || getUserCurrency({ country: userCountry });
  const currencyLabel = (code: string) =>
    ({ NGN: "₦", GHS: "₵", KES: "KSh" })[code] ?? code;
  const [lienAmount, setLienAmount] = useState("");
  const [lienReason, setLienReason] = useState("");
  const [lienReference, setLienReference] = useState("");
  const [lienAction, setLienAction] = useState<string | null>(null);

  const createLien = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (lien.pendingConversion) return;
    const normalizedAmount = lienAmount.trim();
    const amount = Number(normalizedAmount);
    if (!Number.isFinite(amount) || amount <= 0 || lienReason.trim().length < 3)
      return;

    setLienAction("create");
    try {
      await httpCreateUserLien(userID, {
        amount: normalizedAmount,
        currency: fiatCurrency,
        reason: lienReason.trim(),
        reference: lienReference.trim() || undefined,
      });
    } catch {
      showDanger("Could not add lien");
      setLienAction(null);
      return;
    }

    setLienAmount("");
    setLienReason("");
    setLienReference("");
    showSuccess("Lien added successfully");
    try {
      await refetch();
    } catch {
      showDanger("Lien added, but balances could not refresh");
    } finally {
      setLienAction(null);
    }
  };

  const releaseLien = async (lienID: number) => {
    if (!window.confirm("Release this lien? This will restore availability."))
      return;

    setLienAction(`release-${lienID}`);
    try {
      await httpReleaseUserLien(userID, lienID);
    } catch {
      showDanger("Could not release lien");
      setLienAction(null);
      return;
    }

    showSuccess("Lien released successfully");
    try {
      await refetch();
    } catch {
      showDanger("Lien released, but balances could not refresh");
    } finally {
      setLienAction(null);
    }
  };

  return (
    <section className="p-5 mt-8 bg-white border border-orange-200 rounded-lg">
      <h4 className="mb-4 text-lg font-medium">Account lien</h4>

      {lien.pendingConversion ? (
        <p role="status" className="mb-4 text-orange-800">
          This lien is still in NGN. Approve the {fiatCurrency} sell rate on the
          Rates page to convert it. Withdrawals and new liens are blocked until
          conversion; existing liens can still be released.
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="min-w-0 p-4 border border-orange-100 rounded-lg bg-orange-50">
          <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
            Overall wallet balance
          </p>
          <p className="mt-2 text-xl font-medium text-gray-900 whitespace-nowrap tabular-nums">
            {lien.totalPortfolioBalance === null
              ? "Awaiting conversion"
              : `${currencyLabel(fiatCurrency)} ${moneyFormat(lien.totalPortfolioBalance)}`}
          </p>
        </div>
        <div className="min-w-0 p-4 border border-orange-100 rounded-lg bg-orange-50">
          <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
            Active lien
          </p>
          <p className="mt-2 text-xl font-medium text-gray-900 whitespace-nowrap tabular-nums">
            {currencyLabel(lien.lienCurrency ?? fiatCurrency)}{" "}
            {moneyFormat(lien.activeLienAmount)}
          </p>
        </div>
        <div className="min-w-0 p-4 border border-orange-100 rounded-lg bg-orange-50">
          <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
            Available to transact
          </p>
          <p className="mt-2 text-xl font-medium text-gray-900 whitespace-nowrap tabular-nums">
            {currencyLabel(fiatCurrency)} {moneyFormat(lien.availableBalance)}
          </p>
        </div>
        {lien.shortfall !== null && lien.shortfall > 0 ? (
          <div className="min-w-0 p-4 border border-red-100 rounded-lg bg-red-50">
            <p className="text-xs font-medium tracking-wide text-red-600 uppercase">
              Lien shortfall
            </p>
            <p className="mt-2 text-xl font-medium text-red-800 whitespace-nowrap tabular-nums">
              {currencyLabel(fiatCurrency)} {moneyFormat(lien.shortfall)}
            </p>
          </div>
        ) : null}
      </div>

      <form onSubmit={createLien} className="pt-5 mt-5 border-t">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block mb-1 text-sm" htmlFor="lien-amount">
              Amount ({fiatCurrency})
            </label>
            <input
              id="lien-amount"
              type="number"
              min="0.01"
              step="0.01"
              value={lienAmount}
              onChange={(event) => setLienAmount(event.target.value)}
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block mb-1 text-sm" htmlFor="lien-reference">
              Direction/reference (optional)
            </label>
            <input
              id="lien-reference"
              value={lienReference}
              onChange={(event) => setLienReference(event.target.value)}
              className="w-full px-3 py-2 border rounded"
              maxLength={255}
            />
          </div>
        </div>

        <label className="block mt-4 mb-1 text-sm" htmlFor="lien-reason">
          Reason
        </label>
        <textarea
          id="lien-reason"
          value={lienReason}
          onChange={(event) => setLienReason(event.target.value)}
          className="w-full px-3 py-2 border rounded"
          minLength={3}
          maxLength={1000}
          required
        />
        <button
          type="submit"
          disabled={
            lien.pendingConversion ||
            lienAction !== null ||
            Number(lienAmount) <= 0 ||
            lienReason.trim().length < 3
          }
          className="px-4 py-1 mt-4 text-white bg-black rounded-full disabled:cursor-not-allowed disabled:opacity-50"
        >
          {lienAction === "create" ? "Adding..." : "Add lien"}
        </button>
      </form>

      {lien.activeLiens.length > 0 ? (
        <div className="pt-5 mt-5 border-t">
          <h5 className="mb-3 text-sm font-medium">Active lien records</h5>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {lien.activeLiens.map((item) => (
              <div key={item.id} className="p-3 text-sm border rounded">
                <p className="font-medium">
                  {currencyLabel(item.currency ?? fiatCurrency)}{" "}
                  {moneyFormat(Number(item.amount))}
                </p>
                <p className="mt-1 break-words">{item.reason}</p>
                {item.reference ? (
                  <p className="mt-1 text-gray-600 break-words">
                    Ref: {item.reference}
                  </p>
                ) : null}
                <p className="mt-1 text-xs text-gray-500">
                  {DateTime.fromISO(item.createdAt).toFormat("D, t")}
                </p>
                <button
                  type="button"
                  onClick={() => releaseLien(item.id)}
                  disabled={lienAction !== null}
                  className="px-3 py-1 mt-2 text-white bg-red-700 rounded-full disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {lienAction === `release-${item.id}`
                    ? "Releasing..."
                    : "Release lien"}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default UserLien;
